import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_TXIMPORT_RESULT, GET_TXIMPORT_RESULT_ROWS } from '../graphql/queries/tximport';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { tokenRoutePath } from '../utils/tokenRoutes';
import { IMPORTS_RESULT_ROUTE_PREFIX, IMPORTS_REVIEW_ROUTE_PREFIX, IMPORTS_ROUTE, STOCK_IMPORTS_RESULT_ROUTE_PREFIX, STOCK_IMPORTS_REVIEW_ROUTE_PREFIX, STOCK_IMPORTS_ROUTE, STOCK_POSITIONS_ROUTE, stockRoutePath } from '../constants/routes';

const STATUS_STYLES = {
  QUEUED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  WAITING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  RUNNING: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  SUCCEEDED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  TIMEOUT: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  CANCELED: 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-200',
};

const ROW_STATUS_CONFIG = {
  IMPORTED: { label: 'Imported', icon: '✅', color: 'text-green-600 dark:text-green-400' },
  DUPLICATE: { label: 'Duplicate', icon: '🔄', color: 'text-blue-600 dark:text-blue-400' },
  INSUFFICIENT: { label: 'Warning', icon: '⚠️', color: 'text-orange-600 dark:text-orange-400' },
  SKIPPED: { label: 'Skipped', icon: '⏭️', color: 'text-gray-600 dark:text-gray-400' },
  INVALID: { label: 'Invalid', icon: '❌', color: 'text-red-600 dark:text-red-400' },
  PENDING: { label: 'Pending', icon: '⏳', color: 'text-yellow-600 dark:text-yellow-400' },
};

const parseOriginalPayload = (payload) => {
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch (_err) {
    return null;
  }
};

const RowIssues = ({ row }) => {
  const invalid = row.importStatus === 'INVALID';
  const warning = row.importStatus === 'INSUFFICIENT';
  const validationMessages = invalid || row.isValid === false
    ? (row.validationErrors || []).map((issue) => `${issue.field ? `${issue.field}: ` : ''}${issue.message}`)
    : [];
  const messages = [...(row.importError ? [row.importError] : []), ...validationMessages];
  if (!messages.length && !invalid && !warning) return null;
  if (!messages.length) messages.push(invalid
    ? 'The failure reason was not saved for this import. Check the import-worker logs for this row.'
    : 'This row has a holdings warning. The detailed reason was not saved for this import.');
  return (
    <div className={`mt-2 text-xs break-words ${warning ? 'text-orange-700 dark:text-orange-300' : 'text-red-700 dark:text-red-300'}`}>
      <div className="font-medium">{warning ? 'Import warning' : 'Import error'}</div>
      <ul className="mt-1 space-y-1">
        {[...new Set(messages)].map((message) => <li key={message}>{message}</li>)}
      </ul>
    </div>
  );
};

const ImportResultPage = ({ assetKind = 'crypto' }) => {
  const routeIsStock = assetKind === 'stock';
  const importsRoute = routeIsStock ? STOCK_IMPORTS_ROUTE : IMPORTS_ROUTE;
  const reviewRoutePrefix = routeIsStock ? STOCK_IMPORTS_REVIEW_ROUTE_PREFIX : IMPORTS_REVIEW_ROUTE_PREFIX;
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { currentPortfolio } = usePortfolioContext();
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const pageSize = 50;

  const { data, loading, error, refetch } = useQuery(GET_TXIMPORT_RESULT, {
    variables: { jobId },
    skip: !jobId,
    pollInterval: 5000,
  });

  // Redirect if job belongs to a different portfolio or route namespace
  useEffect(() => {
    const portfolioId = data?.tximportResult?.portfolioId;
    if (portfolioId && currentPortfolio?.id && portfolioId !== currentPortfolio.id) {
      toast.error('This job belongs to a different portfolio');
      navigate(importsRoute);
      return;
    }
    const resultAssetKind = data?.tximportResult?.assetKind;
    if (resultAssetKind) {
      const jobIsStock = resultAssetKind === 'stock';
      if (jobIsStock !== routeIsStock) {
        const prefix = jobIsStock ? STOCK_IMPORTS_RESULT_ROUTE_PREFIX : IMPORTS_RESULT_ROUTE_PREFIX;
        navigate(`${prefix}/${jobId}`, { replace: true });
      }
    }
  }, [data?.tximportResult?.portfolioId, data?.tximportResult?.assetKind, currentPortfolio?.id, navigate, importsRoute, routeIsStock, jobId]);

  const { data: rowsData, loading: rowsLoading, error: rowsError, refetch: refetchRows } = useQuery(GET_TXIMPORT_RESULT_ROWS, {
    variables: { 
      jobId, 
      status: statusFilter, 
      limit: pageSize, 
      offset: page * pageSize 
    },
    skip: !jobId,
  });

  const result = data?.tximportResult;
  const summary = result?.summary;
  const statusKey = result?.status ? result.status.toUpperCase() : null;
  const showReviewButton = result?.stage === 'REVIEWING' && statusKey !== 'CANCELED';
  const isStock = result?.assetKind === 'stock';

  const rows = rowsData?.tximportResultRows?.items ?? [];
  const totalRows = rowsData?.tximportResultRows?.total ?? 0;
  const hasMore = rowsData?.tximportResultRows?.hasMore ?? false;
  const summaryRevision = summary
    ? [summary.total, summary.imported, summary.duplicates, summary.insufficient, summary.userSkipped, summary.invalid, summary.pending].join(':')
    : null;

  // Summary and rows are separate queries: discard stale pending row results
  // when batches change the summary as well as at final completion. Reuse the
  // existing summary polling; do not create a second polling timer.
  useEffect(() => {
    if (statusKey) refetchRows().catch(() => {});
  }, [statusKey, summaryRevision, refetchRows]);

  const handleRefresh = async () => {
    try {
      await Promise.all([refetch(), refetchRows()]);
      toast.success('Refreshed');
    } catch (err) {
      console.error('Failed to refresh', err);
      toast.error('Refresh failed');
    }
  };

  const handleFilterChange = (filter) => {
    setStatusFilter(filter);
    setPage(0);
  };

  if (!jobId) {
    return (
      <div className="px-2 sm:px-3 lg:px-4 py-2">
        <ErrorAlert message="Missing job identifier" />
      </div>
    );
  }

  if (loading && !result) {
    return (
      <div className="py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-2 sm:px-3 lg:px-4 py-2">
        <ErrorAlert message="Failed to load import result" />
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{isStock ? 'Stock Import Result' : 'Import Result'}</h1>
          <Link to={importsRoute} className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            ← Back to Tasks
          </Link>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0">
            {showReviewButton && (
              <Link
                to={`${reviewRoutePrefix}/${jobId}`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              >
                Review import
              </Link>
            )}
            {isStock && statusKey === 'SUCCEEDED' && (
              <Link
                to={STOCK_POSITIONS_ROUTE}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
              >
                View stock positions
              </Link>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
            >
              Refresh
            </button>
          </div>
      </div>
      <div>
        <p className="text-gray-600 dark:text-gray-400">
          Job ID: <span className="font-mono text-sm break-all">{jobId}</span>
        </p>
        <div className="flex flex-wrap items-center gap-3 text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <span className="text-sm">Status:</span>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                STATUS_STYLES[statusKey] || 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-200'
              }`}
            >
              {statusKey || '—'}
            </span>
          </div>
          {result?.stage ? (
            <span className="text-sm text-gray-600 dark:text-gray-400">Stage: {result.stage}</span>
          ) : null}
        </div>
        {/* Error message for failed jobs */}
        {result?.error && (statusKey === 'FAILED' || statusKey === 'TIMEOUT') && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Import Failed</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{result.error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <SummaryCard 
            label="Total" 
            value={summary.total} 
            onClick={() => handleFilterChange(null)}
            active={statusFilter === null}
          />
          <SummaryCard 
            label="Imported" 
            value={summary.imported} 
            icon="✅"
            onClick={() => handleFilterChange('IMPORTED')}
            active={statusFilter === 'IMPORTED'}
          />
          <SummaryCard 
            label="Duplicates" 
            value={summary.duplicates} 
            icon="🔄"
            onClick={() => handleFilterChange('DUPLICATE')}
            active={statusFilter === 'DUPLICATE'}
          />
          <SummaryCard 
            label="Warnings"
            value={summary.insufficient} 
            icon="⚠️"
            onClick={() => handleFilterChange('INSUFFICIENT')}
            active={statusFilter === 'INSUFFICIENT'}
          />
          <SummaryCard 
            label="Skipped" 
            value={summary.userSkipped} 
            icon="⏭️"
            onClick={() => handleFilterChange('SKIPPED')}
            active={statusFilter === 'SKIPPED'}
          />
          <SummaryCard 
            label="Invalid" 
            value={summary.invalid} 
            icon="❌"
            onClick={() => handleFilterChange('INVALID')}
            active={statusFilter === 'INVALID'}
          />
          <SummaryCard 
            label="Pending" 
            value={summary.pending} 
            icon="⏳"
            onClick={() => handleFilterChange('PENDING')}
            active={statusFilter === 'PENDING'}
          />
        </div>
      )}

      {summary && (summary.invalid > 0 || summary.insufficient > 0) && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Review issues are found before import. Additional rows can fail during transaction creation or holdings calculation.
          {' '}Select Invalid or Warnings to see affected rows and their reasons.
        </p>
      )}

      {/* Results Table */}
      <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
              Import Rows
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              {totalRows.toLocaleString()} {statusFilter ? ROW_STATUS_CONFIG[statusFilter]?.label.toLowerCase() : 'total'} rows
            </p>
          </div>
        </div>
        
        {rowsError ? (
          <div className="p-4"><ErrorAlert message="Failed to load import rows and error details. Try Refresh." /></div>
        ) : rowsLoading ? (
          <div className="py-12">
            <LoadingSpinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">
            No rows found
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700 md:hidden">
                {rows.map((row) => {
                  const statusConfig = ROW_STATUS_CONFIG[row.importStatus] || ROW_STATUS_CONFIG.PENDING;
                  const isExpanded = expandedRowId === row.id;
                  const parsedOriginal = parseOriginalPayload(row.originalRow);
                  const sourceHeaders = Array.isArray(parsedOriginal?.headers) ? parsedOriginal.headers : [];
                  const sourceValues = Array.isArray(parsedOriginal?.values) ? parsedOriginal.values : [];
                  const sourcePairs = sourceHeaders.map((header, idx) => ({ header, value: sourceValues[idx] ?? '' }));
                  const tokenLabel = row.token?.ticker || row.tokenId || row.rawIsin || '—';
                  const tokenName = row.token?.name || '';
                  const tokenContent = isStock && row.stockId && row.token?.ticker ? (
                    <Link
                      to={stockRoutePath(row.token.ticker)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                    >
                      {tokenLabel}
                    </Link>
                  ) : row.token?.coingeckoApiId || row.token?.coingeckoSlug ? (
                    <Link
                      to={tokenRoutePath(row.token)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                    >
                      {tokenLabel}
                    </Link>
                  ) : tokenLabel;

                  return (
                    <div key={row.id} className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 text-left">
                          <div className="flex items-center gap-2">
                            <span className={statusConfig.color} title={statusConfig.label}>{statusConfig.icon}</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{tokenContent}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{statusConfig.label}</span>
                          </div>
                          {tokenName && <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{tokenName}</div>}
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {row.transactionType?.toLowerCase() || '—'} · {formatNumber(row.quantity)} @ {formatNumber(row.price)} · {row.currency || '—'}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {formatTimestamp(row.timestamp)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                          className="shrink-0 text-xs text-blue-600 dark:text-blue-400"
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? 'Hide' : 'Details'}
                        </button>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <div><span className="text-gray-500 dark:text-gray-400">Location</span><br />{row.rawLocation || '—'}</div>
                        {!isStock && (
                          <>
                            <div><span className="text-gray-500 dark:text-gray-400">From</span><br />{row.rawSourceLocation || '—'}</div>
                            <div><span className="text-gray-500 dark:text-gray-400">To</span><br />{row.rawDestinationLocation || '—'}</div>
                          </>
                        )}
                      </div>
                      <RowIssues row={row} />
                      {isExpanded && (
                        <div className="mt-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Original Values</h4>
                          {sourcePairs.length === 0 ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400">No source data captured</div>
                          ) : (
                            <div className="space-y-2">
                              {sourcePairs.map((pair, idx) => (
                                <div key={idx} className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 text-xs">
                                  <div className="font-mono text-gray-500 dark:text-gray-400 break-words">{pair.header || '—'}</div>
                                  <div className="mt-1 text-gray-900 dark:text-gray-100 break-words">{pair.value || '—'}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {parsedOriginal?.source && (
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Source format: {parsedOriginal.source}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <Th>Status</Th>
                    <Th>{isStock ? 'Instrument' : 'Token'}</Th>
                    <Th>Type</Th>
                    <Th>Quantity</Th>
                    <Th>Price</Th>
                    <Th>Timestamp</Th>
                    <Th>Currency</Th>
                    <Th>Location</Th>
                    {!isStock && <Th>From</Th>}
                    {!isStock && <Th>To</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  {rows.map((row) => {
                    const statusConfig = ROW_STATUS_CONFIG[row.importStatus] || ROW_STATUS_CONFIG.PENDING;
                    const isExpanded = expandedRowId === row.id;
                    const parsedOriginal = parseOriginalPayload(row.originalRow);
                    const sourceHeaders = Array.isArray(parsedOriginal?.headers) ? parsedOriginal.headers : [];
                    const sourceValues = Array.isArray(parsedOriginal?.values) ? parsedOriginal.values : [];
                    const sourcePairs = sourceHeaders.map((header, idx) => ({
                      header,
                      value: sourceValues[idx] ?? '',
                    }));
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedRowId(isExpanded ? null : row.id);
                            }
                          }}
                        >
                          <Td>
                            <span className={statusConfig.color} title={statusConfig.label}>
                              {statusConfig.icon}
                            </span>
                          </Td>
                          <Td>
                            {isStock && row.stockId && row.token?.ticker ? (
                              <span onClick={(e) => e.stopPropagation()}>
                                <Link
                                  to={stockRoutePath(row.token.ticker)}
                                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                                >
                                  {row.token.ticker}
                                </Link>
                              </span>
                            ) : isStock ? (
                              <span className="font-mono">{row.rawIsin || '—'}</span>
                            ) : row.token?.coingeckoApiId || row.token?.coingeckoSlug ? (
                              <span
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Link 
                                  to={tokenRoutePath(row.token)}
                                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                                >
                                  {row.token?.ticker || row.tokenId || '—'}
                                </Link>
                              </span>
                            ) : (
                              row.token?.ticker || row.tokenId || '—'
                            )}
                            {row.token?.name && (
                              <span className="text-gray-400 text-xs ml-1">({row.token.name})</span>
                            )}
                          </Td>
                          <Td className="capitalize">{row.transactionType?.toLowerCase()}</Td>
                          <Td>{formatNumber(row.quantity)}</Td>
                          <Td>{formatNumber(row.price)}</Td>
                          <Td>{formatTimestamp(row.timestamp)}</Td>
                          <Td>{row.currency}</Td>
                          <Td>{row.rawLocation || '—'}</Td>
                          {!isStock && <Td>{row.rawSourceLocation || '—'}</Td>}
                          {!isStock && <Td>{row.rawDestinationLocation || '—'}</Td>}
                        </tr>
                        {(row.importStatus === 'INVALID' || row.importStatus === 'INSUFFICIENT' || row.importError) && (
                          <tr>
                            <td colSpan={isStock ? 8 : 10} className="px-6 pb-3">
                              <RowIssues row={row} />
                            </td>
                          </tr>
                        )}
                        {isExpanded && (
                          <tr className="bg-gray-50 dark:bg-gray-900/50">
                            <td colSpan={isStock ? 8 : 10} className="px-6 py-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Original Values</h4>
                                {sourcePairs.length === 0 ? (
                                  <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-md">
                                    No source data captured
                                  </div>
                                ) : (
                                  <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                    <table className="min-w-full text-sm">
                                      <thead className="bg-gray-100 dark:bg-gray-800/50">
                                        <tr>
                                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                                            Column
                                          </th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                                            Value
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {sourcePairs.map((pair, idx) => (
                                          <tr key={idx}>
                                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-mono">
                                              {pair.header || '—'}
                                            </td>
                                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100 break-words">
                                              {pair.value || '—'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {parsedOriginal?.source && (
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    Source format: {parsedOriginal.source}
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            
            {/* Pagination - only show when more than 1 page */}
            {totalRows > pageSize && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={!hasMore}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{page * pageSize + 1}</span> to{' '}
                      <span className="font-medium">{Math.min((page + 1) * pageSize, totalRows)}</span> of{' '}
                      <span className="font-medium">{totalRows.toLocaleString()}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={!hasMore}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, icon, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    className={`bg-white dark:bg-gray-800 shadow sm:rounded-lg p-4 text-left transition-all ${
      active 
        ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-900' 
        : 'hover:shadow-md'
    }`}
  >
    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
      {icon && <span>{icon}</span>}
      {label}
    </p>
    <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
      {Number(value).toLocaleString()}
    </p>
  </button>
);

const Th = ({ children }) => (
  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
    {children}
  </th>
);

const Td = ({ children, className = '' }) => (
  <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white ${className}`}>
    {children}
  </td>
);

const formatNumber = (value) => {
  if (!value) return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  if (num === 0) return '0';
  if (Math.abs(num) < 0.0001) return num.toExponential(4);
  if (Math.abs(num) < 1) return num.toFixed(8).replace(/\.?0+$/, '');
  return num.toLocaleString(undefined, { maximumFractionDigits: 8 });
};

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

export default ImportResultPage;
